import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Trash } from "@phosphor-icons/react";
import ViewerBanner from "@/components/ViewerBanner";
import { useIsViewer } from "@/lib/permissions";
import { toast } from "sonner";

export default function Team() {
  const viewer = useIsViewer();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", password: "", allowed_venues: "", sub_role: "manager" });

  const load = () => api.get("/company/team").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    try {
      await api.post("/company/team", {
        ...form,
        allowed_venues: form.allowed_venues.split(",").map(s=>s.trim()).filter(Boolean),
      });
      toast.success("Manager added");
      setOpen(false);
      setForm({ email: "", first_name: "", last_name: "", password: "", allowed_venues: "", sub_role: "manager" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try { await api.delete(`/company/team/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      {viewer && <ViewerBanner />}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Users size={28} className="text-blue-600" />
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Team</h1>
        </div>
        {!viewer && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="team-invite-open" className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-1"/> Add manager</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite a manager</DialogTitle><DialogDescription>Create a scoped sub-account. Managers only see the venues you allow.</DialogDescription></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">First name</Label><Input data-testid="team-first" value={form.first_name} onChange={(e)=>setForm({...form, first_name: e.target.value})} className="h-11 mt-1.5"/></div>
                <div><Label className="label-caps">Last name</Label><Input data-testid="team-last" value={form.last_name} onChange={(e)=>setForm({...form, last_name: e.target.value})} className="h-11 mt-1.5"/></div>
              </div>
              <div><Label className="label-caps">Email</Label><Input data-testid="team-email" type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="h-11 mt-1.5"/></div>
              <div><Label className="label-caps">Temporary password</Label><Input data-testid="team-password" type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} className="h-11 mt-1.5"/></div>
              <div><Label className="label-caps">Allowed venues (postcodes / cities, comma separated)</Label><Input data-testid="team-venues" value={form.allowed_venues} onChange={(e)=>setForm({...form, allowed_venues: e.target.value})} className="h-11 mt-1.5" placeholder="M1 1AA, Manchester"/></div>
              <div>
                <Label className="label-caps">Role</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <label className={`p-3 rounded-md border-2 cursor-pointer text-sm ${form.sub_role === "manager" ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
                    <input type="radio" className="mr-2" checked={form.sub_role === "manager"} onChange={()=>setForm({...form, sub_role: "manager"})} data-testid="team-role-manager"/>
                    <strong>Manager</strong><div className="text-xs text-slate-600 mt-0.5">Can create jobs, mark attendance, verify photos.</div>
                  </label>
                  <label className={`p-3 rounded-md border-2 cursor-pointer text-sm ${form.sub_role === "viewer" ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
                    <input type="radio" className="mr-2" checked={form.sub_role === "viewer"} onChange={()=>setForm({...form, sub_role: "viewer"})} data-testid="team-role-viewer"/>
                    <strong>Viewer</strong><div className="text-xs text-slate-600 mt-0.5">Read-only. Cannot edit anything.</div>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button data-testid="team-invite-btn" onClick={invite} className="bg-blue-600 hover:bg-blue-700">Add manager</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>
      <p className="text-slate-600">Team members can manage shifts scoped to the venues you assign.</p>
      <div className="space-y-2">
        {items.length === 0 && <div className="card-tactile p-8 text-center text-slate-500">No team members yet.</div>}
        {items.map((m) => (
          <div key={m.id} className="card-tactile p-5 flex items-center justify-between gap-3" data-testid={`team-${m.id}`}>
            <div>
              <div className="font-display text-lg font-bold text-slate-900">{m.first_name} {m.last_name}</div>
              <div className="text-sm text-slate-600">{m.email}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {(m.allowed_venues || []).length === 0 ? <span className="text-xs text-slate-500">All venues</span> :
                  (m.allowed_venues || []).map((v) => <span key={v} className="px-2 py-0.5 rounded-md bg-slate-100 text-xs font-semibold">{v}</span>)}
              </div>
            </div>
            <Button data-testid={`team-remove-${m.id}`} variant="outline" size="sm" disabled={viewer} onClick={()=>!viewer && remove(m.id)} className="text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"><Trash size={14}/></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
